/**
 * Sentry Error Tracking Integration for BeetleSense.ai
 *
 * Every exception capture includes an error code tag.
 * PII filtering applied at init via beforeSend hook.
 * See /docs/error-codes.md for the full code catalog.
 */

import * as Sentry from '@sentry/react';
import { ERROR_CODES, type AppError } from './errorCodes';
import { logErrorToSupabase } from './errorLogger';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string;

let initialized = false;

/**
 * Strip PII from various event data structures
 * - URLs with @ (email)
 * - Request bodies containing password fields
 * - Sensitive headers
 */
function sanitizeEventPii(event: Sentry.Event | Sentry.ErrorEvent): Sentry.Event | Sentry.ErrorEvent {
  // Strip email from URLs
  if (event.request?.url) {
    event.request.url = event.request.url.replace(
      /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+)/g,
      '[REDACTED_EMAIL]'
    );
  }

  // Strip sensitive headers
  if (event.request?.headers) {
    const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'x-auth-token'];
    sensitiveHeaders.forEach((header) => {
      const key = Object.keys(event.request!.headers || {}).find(
        (k) => k.toLowerCase() === header
      );
      if (key) {
        event.request!.headers![key] = '[REDACTED]';
      }
    });
  }

  // Redact request body for password/token fields
  if (event.request?.data && typeof event.request.data === 'string') {
    try {
      const body = JSON.parse(event.request.data);
      const sensitiveFields = ['password', 'token', 'api_key', 'secret', 'email'];
      sensitiveFields.forEach((field) => {
        if (field in body) {
          body[field] = '[REDACTED]';
        }
      });
      event.request.data = JSON.stringify(body);
    } catch {
      // If not JSON, leave as-is
    }
  }

  // Strip PII from breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      data: b.data
        ? sanitizeBreadcrumbData(b.data as Record<string, unknown>)
        : undefined,
    }));
  }

  return event;
}

function sanitizeBreadcrumbData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized = { ...data };
  const sensitiveFields = ['email', 'password', 'token', 'api_key', 'phone'];

  sensitiveFields.forEach((field) => {
    if (field in sanitized) {
      sanitized[field] = '[REDACTED]';
    }
  });

  return sanitized;
}

export function initSentry(): void {
  if (initialized || !SENTRY_DSN || SENTRY_DSN === 'https://your-dsn@sentry.io/0') {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info('[Sentry] Skipped — no valid DSN. Set VITE_SENTRY_DSN in .env');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `beetlesense@${import.meta.env.VITE_APP_VERSION || '0.1.0'}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event: Sentry.ErrorEvent) {
      // CRITICAL: Apply PII filtering to all events before sending to Sentry
      // Never send email, phone, passwords, or sensitive auth tokens
      return sanitizeEventPii(event) as Sentry.ErrorEvent | null;
    },
  });

  initialized = true;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[Sentry] Initialized with PII filtering');
  }
}

export function setSentryUser(userId: string, role?: string): void {
  if (!initialized) return;
  // NOTE: email field intentionally omitted to prevent PII leakage
  // Only user ID (UUID) and role are sent — never raw email
  Sentry.setUser({ id: userId, role });
}

export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * Capture an exception with the BeetleSense error code system.
 * Every Sentry event includes the error code as a tag.
 */
export function captureWithCode(
  error: unknown,
  code: string,
  extra?: Record<string, unknown>,
): void {
  const appError: AppError | undefined = ERROR_CODES[code];
  const err = error instanceof Error ? error : new Error(String(error));

  // Always log to Supabase error_logs table (even if Sentry is not initialized)
  logErrorToSupabase(code, err.message, err.stack, extra);

  if (!initialized) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(`[Sentry] Would capture [${code}]:`, err.message, extra);
    }
    return;
  }

  Sentry.captureException(err, {
    tags: {
      code,
      module: appError?.module || 'UNKNOWN',
    },
    extra: {
      errorCode: code,
      userMessage: appError?.userMessage,
      action: appError?.action,
      ...extra,
    },
  });
}

/**
 * Capture a message (non-exception) with error code.
 */
export function captureMessage(message: string, code: string, level: Sentry.SeverityLevel = 'warning'): void {
  if (!initialized) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[Sentry] Would capture message [${code}]: ${message}`);
    }
    return;
  }

  Sentry.captureMessage(message, {
    level,
    tags: { code, module: ERROR_CODES[code]?.module || 'UNKNOWN' },
  });
}

export { Sentry };
